import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { formatEUR } from "@/lib/format";
import { FadeUp } from "@/components/Motion";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const { t, lang } = useI18n();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user) api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (loading) return <div className="pt-40 text-center text-cream/50">Loading...</div>;
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="pt-32 pb-32 min-h-screen" data-testid="account-page">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <FadeUp className="mb-12 flex justify-between items-end flex-wrap gap-4">
          <div>
            <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-4">/ {t.account.t}</div>
            <h1 className="font-editorial italic font-light text-5xl lg:text-6xl text-cream">Hello, {user.name}</h1>
          </div>
          <button onClick={logout} data-testid="logout-button" className="font-display text-[10px] tracking-[0.3em] text-cream/60 hover:text-gold link-underline">→ {t.nav.logout}</button>
        </FadeUp>

        {user.is_admin && (
          <FadeUp className="mb-8">
            <Link to="/admin" data-testid="link-admin" className="inline-block border border-gold/40 text-gold px-6 py-3 font-display text-[10px] tracking-[0.3em] hover:bg-gold hover:text-black transition-colors">→ {t.nav.admin}</Link>
          </FadeUp>
        )}

        <FadeUp>
          <h2 className="font-display text-[10px] tracking-[0.3em] text-gold mb-8">/ {t.account.orders}</h2>
          {orders.length === 0 ? (
            <p className="text-cream/50 font-body">{t.account.empty}</p>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.session_id} className="border border-white/10 p-6 flex justify-between items-center flex-wrap gap-4" data-testid={`order-${o.session_id}`}>
                  <div>
                    <div className="font-display text-[10px] tracking-[0.3em] text-cream/60 mb-1">{new Date(o.created_at).toLocaleDateString()}</div>
                    <div className="text-cream text-sm font-body">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(" · ")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-editorial text-2xl text-gold">{formatEUR((o.amount || 0) / 100, lang)}</div>
                    <div className={`font-display text-[9px] tracking-[0.3em] ${o.payment_status === "paid" ? "text-gold" : "text-cream/50"}`}>{o.payment_status?.toUpperCase()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FadeUp>
      </div>
    </div>
  );
}
